import { RefObject, useEffect } from "react";

interface UseModalScrollIndicatorOptions {
  isModalActive: boolean;
  scrollIndicatorRef: RefObject<HTMLElement>;
  modalContentRef?: RefObject<HTMLElement>;
  scrollThreshold?: number;
  initialDelay?: number;
}

/**
 * Hook for managing scroll indicators in modal dialogs.
 *
 * Automatically shows/hides a scroll indicator based on scroll position
 * and whether the content is scrollable.
 *
 * **Features:**
 * - Shows indicator when modal content is scrollable
 * - Hides indicator when scrolled near bottom
 * - Responds to content resize
 * - Performance-optimized with RAF
 *
 * @param options Configuration for modal scroll indicator
 *
 * @example
 * ```tsx
 * useModalScrollIndicator({
 *   isModalActive: isOpen,
 *   scrollIndicatorRef: indicatorRef,
 *   modalContentRef: contentRef,
 *   scrollThreshold: 15,
 *   initialDelay: 250
 * });
 * ```
 */
export const useModalScrollIndicator = ({
  isModalActive,
  scrollIndicatorRef,
  modalContentRef,
  scrollThreshold = 15,
  initialDelay = 250
}: UseModalScrollIndicatorOptions) => {
  useEffect(() => {
    if (!isModalActive || !scrollIndicatorRef.current) {
      return;
    }

    const indicator = scrollIndicatorRef.current;

    const scrollableElement = document.querySelector(
      "[data-modal-scrollable-content]"
    ) as HTMLElement;

    if (!scrollableElement) {
      // Hide indicator if we can't find the scrollable element
      indicator.classList.add("!opacity-0");
      return;
    }

    let ticking = false;

    const updateScrollIndicator = () => {
      const scrollTop = scrollableElement.scrollTop;
      const scrollHeight = scrollableElement.scrollHeight;
      const clientHeight = scrollableElement.clientHeight;

      // Check if content can scroll
      // For max-height scenarios, we need to check if there's actually scrollable content
      // even when scrollHeight equals clientHeight
      const computedStyle = window.getComputedStyle(scrollableElement);
      const hasMaxHeight = computedStyle.maxHeight && computedStyle.maxHeight !== "none";
      const hasOverflowY =
        computedStyle.overflowY === "auto" || computedStyle.overflowY === "scroll";

      // If we have max-height and overflow, and the element can potentially scroll
      // then show the indicator even if scrollHeight === clientHeight
      const hasScrollableContent =
        scrollHeight > clientHeight ||
        (hasMaxHeight &&
          hasOverflowY &&
          scrollableElement.scrollHeight >= scrollableElement.clientHeight);

      if (!hasScrollableContent) {
        // If content doesn't overflow, hide the indicator
        indicator.classList.add("!opacity-0");
        return;
      }

      // Show indicator by default when content can scroll
      indicator.classList.remove("!opacity-0");

      // Hide when scrolled near the bottom (within threshold of bottom)
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - scrollThreshold;

      if (isNearBottom) {
        indicator.classList.add("!opacity-0");
      } else {
        indicator.classList.remove("!opacity-0");
      }

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateScrollIndicator);
        ticking = true;
      }
    };

    // Initial check to set correct state
    const initialTimeout = setTimeout(() => {
      updateScrollIndicator();
    }, initialDelay);

    scrollableElement.addEventListener("scroll", handleScroll, { passive: true });

    // Also listen for resize events in case content changes
    const resizeObserver = new ResizeObserver(() => {
      updateScrollIndicator();
    });

    resizeObserver.observe(scrollableElement);

    return () => {
      clearTimeout(initialTimeout);
      scrollableElement.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [isModalActive, scrollIndicatorRef, modalContentRef, scrollThreshold, initialDelay]);
};
